
import * as angular from "angular";
import { IDeleteVertexParams } from "@interfaces/IDeleteVertexParams";
import { IGeofence } from "@interfaces/IGeofences";
import { GeofenceService } from "@services/geofence.service";
import { IOnChangesObject } from "angular";
import { GeofenceType } from "@enums/GeofenceType";
import "../geofenceOverlay.scss";
import "./geofenceShape.component.scss";
import { GoogleMarkerService } from "@services/googleMarker.service";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";

export class GeofenceShapeComponentController implements angular.IComponentController {

    // bindings
    geofence: IGeofence;
    geofenceType: GeofenceType;
    editable: boolean = false;
    ignoreMaxSize: boolean = false;

    onChange: ({ ignoringMaxSize: boolean }) => void;

    veriMap: IVeriMapComponent;
    map: google.maps.Map;

    geofenceVisual: google.maps.Circle | google.maps.Polygon;

    // size restrictions
    maxRadius: number = 2000;
    maxDiameter: number;
    minRadius: number;
    minDiameter: number;
    minPolygonArea: number;
    maxPolygonArea: number;

    static $inject = ["$scope", "geofenceService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly geofenceService: GeofenceService) {
    }

    $onInit() {
        this.prepareMaximumSize();

        this.veriMap.mapInitialisedPromise.then(map => {
            this.map = map;
            this.createGeofenceVisual();
        });
    }

    $onChanges(onChangesObj: IOnChangesObject): void {
        if (!this.veriMap.mapInitialised) {
            return;
        }

        if (this.geofence) {
            this.geofenceType = Number(this.geofenceType);
        }

        if (onChangesObj["removeMaxSize"]) {
            this.prepareMaximumSize();
        }

        if (onChangesObj["geofenceType"] || onChangesObj["geofence"]) {
            if (this.geofence) {
                this.geofence.GeofenceType = this.geofenceType;
                this.createGeofenceVisual();
            }
        }

        if (this.geofence && !this.geofence.Addr1) {
            this.geofenceService.setGeofenceAddressFromPosition(this.geofence);
        }
    }

    $onDestroy() {
        this.geofenceService.destroyGeofence(this.geofenceVisual);
    }

    private prepareMaximumSize(): void {
        this.maxRadius = (this.ignoreMaxSize) ? 99999 : 2000;
        this.maxDiameter = this.maxRadius * 2;
        this.minRadius = 50;
        this.minDiameter = this.minRadius; // CJE Changed at customer request to be half of what it used to be - Seb implemented
        this.minPolygonArea = this.minDiameter * this.minDiameter;
        this.maxPolygonArea = this.maxDiameter * this.maxDiameter;
    }

    private createGeofenceVisual() {
        this.geofenceService.removeDeleteVertexButtonFromPolygon();
        this.geofenceService.destroyGeofence(this.geofenceVisual);

        if (!this.geofence) {
            return;
        }

        if (this.geofence.GeofenceType == GeofenceType.Circle) {
            if (!this.geofence.Radius) {
                this.geofence.Radius = 100;
            }

            const circle = new google.maps.Circle({
                map: this.map,
                radius: this.geofence.Radius,
                center: new google.maps.LatLng(this.geofence.Latitude, this.geofence.Longitude)
            });

            this.geofenceVisual = circle;

            // Always make sure we have corresponding polygon points
            if (!this.geofence.Points?.length) {
                this.createBasicSquarePolygon(circle.getBounds());
            }
        }
        else {
            var path: google.maps.LatLng[] = [];

            this.geofence?.Points?.forEach(p => {
                path.push(new google.maps.LatLng(p[0], p[1]));
            });

            const polygon = new google.maps.Polygon({
                map: this.map,
                paths: path
            });

            this.geofenceVisual = polygon;
        }

        this.geofenceVisual.setOptions((this.editable) ? this.geofenceService.selectedGeofenceColors : this.geofenceService.defaultGeofenceColors);
        this.setGeofenceEditable(true);

        this.geofenceChanged();

        if (!this.geofenceVisual.getMap()) {
            this.geofenceVisual.setMap(this.veriMap.getMap());
        }
    }

    private createBasicSquarePolygon(bounds: google.maps.LatLngBounds) {
        this.geofence.Points = [];
        const latlngs = this.getPolygonFromBoundingBox(bounds);

        latlngs.forEach(latlng => {
            this.geofence.Points.push([latlng.lat(), latlng.lng()]);
        });
    }

    private geofenceChanged() {
        if (this.onChange) {
            this.onChange({ ignoringMaxSize: this.ignoreMaxSize });
        }
    }

    private getPolygonFromBoundingBox(bounds: google.maps.LatLngBounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const se = new google.maps.LatLng(sw.lat(), ne.lng());
        const nw = new google.maps.LatLng(ne.lat(), sw.lng());

        return [ne, se, sw, nw];
    }

    private polygonUpdated() {
        this.updateGeofenceModelFromVisual();
        this.geofenceService.setPolygonGeofenceVisualCenter(this.geofence);
    }

    private updateGeofenceModelFromVisual() {
        if (this.geofence.GeofenceType == GeofenceType.Circle) {

            this.syncGeofenceTypes();

            var circle = this.geofenceVisual as google.maps.Circle;
            var circleCenter = circle.getCenter();

            this.geofence.Latitude = circleCenter.lat();
            this.geofence.Longitude = circleCenter.lng();
            this.geofence.Radius = circle.getRadius();
        }
        else {
            this.geofence.Points = [];
            var polygon = this.geofenceVisual as google.maps.Polygon;
            var pathArray: google.maps.LatLng[] = polygon.getPath().getArray();

            pathArray.forEach(p => {
                this.geofence.Points.push([p.lat(), p.lng()]);
            });

            this.syncGeofenceTypes();
        }
    }

    private setGeofenceEditable(setting: boolean) {
        const editable = setting && this.editable;
        this.geofenceVisual.setDraggable(editable);
        this.geofenceVisual.setEditable(editable);
        this.geofenceVisual.setOptions({ clickable: editable });

        if (!editable) {
            return;
        }

        google.maps.event.addListener(this.geofenceVisual, "radius_changed", () => {
            this.updateGeofenceModelFromVisual();
            this.geofenceChanged();
            this.$scope.$apply();
        });

        google.maps.event.addListener(this.geofenceVisual, "center_changed", () => {
            this.updateGeofenceModelFromVisual();
            this.geofenceChanged();
            this.$scope.$apply();
        });

        // On drag end address will be updated
        google.maps.event.addListener(this.geofenceVisual, "dragend", () => {
            this.updateGeofenceModelFromVisual();
            this.geofenceService.setGeofenceAddressFromPosition(this.geofence);
            this.geofenceChanged();
            this.$scope.$apply();
        });

        if (this.geofence.GeofenceType === GeofenceType.Polygon) {
            const poly = this.geofenceVisual as google.maps.Polygon;
            const deleteVertexParams: IDeleteVertexParams = { polygon: poly, geofence: this.geofence };
            this.geofenceService.addDeleteVertexButtonToPolygon(deleteVertexParams);

            const path = poly.getPath();

            google.maps.event.addListener(path, "set_at", (index: number) => {
                this.polygonUpdated();
                this.geofenceChanged();
                this.$scope.$apply();
            });
            google.maps.event.addListener(path, "insert_at", (index: number) => {
                this.polygonUpdated();
                this.geofenceChanged();
                this.$scope.$apply();
            });
        }
    }

    private syncGeofenceTypes() {
        if (!this.geofence || !this.geofenceVisual) {
            return;
        }

        if (this.geofence.GeofenceType == GeofenceType.Circle) {

            if (this.geofence.Points) {
                let circleVisual = this.geofenceVisual as google.maps.Circle;
                if (!circleVisual || !circleVisual.getBounds) {
                    this.createGeofenceVisual();
                    circleVisual = this.geofenceVisual as google.maps.Circle;
                }
                // move points by the same amount as the circle was moved
                const circleCenter = circleVisual.getCenter();

                var differenceLat = circleCenter.lat() - this.geofence.Latitude;
                var differenceLng = circleCenter.lng() - this.geofence.Longitude;

                this.geofence.Points.forEach(p => {
                    p[0] += differenceLat;
                    p[1] += differenceLng;
                });
            }
            else {
                this.createGeofenceVisual();
            }
        }
        else {
            this.geofenceService.setPolygonGeofenceVisualCenter(this.geofence);
        }
    }
}

export class GeofenceShapeComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;
    require: { [index: string]: string; };

    constructor() {
        this.bindings = {
            geofence: "<",
            geofenceType: "<",
            editable: "<?",
            ignoreMaxSize: "<?",

            onChange: "&",
        };

        this.require = {
            veriMap: "^veriMap"
        };

        this.controller = GeofenceShapeComponentController;
        this.template = require("./geofenceShape.component.html");
    }
}
